import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  X, ChevronUp, ChevronDown, Save, Send, Building, Calculator,
  FileText, Users, Briefcase,
} from 'lucide-react';
import CustomSelect from '../../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../../shared/components/custom/CustomNumberInput';
import { formatBrandMoney } from '../../../shared/utils/brandCurrency';
import { logger } from '../../../shared/utils/logger';
import {
  SAUDI_ZAKAT_RATE,
  SAUDI_CIT_RATE,
  SAUDI_WHT_RATES,
  validateTRN,
  formatTRN,
} from './saudiTaxConfig';
import RequiredLabelText from '../../../shared/components/custom/RequiredLabelText';

const FIELD = 'w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all';
const LABEL = 'block w-full text-white/70 text-sm mb-2 font-medium text-right';
const NUMBER_FIELD = `${FIELD} text-right font-mono`;

export const FiscalModalShell = ({ open, onClose, title, Icon, iconClass = 'text-blue-400', children, footer, maxWidth = '1200px', zIndex = 99999 }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4" style={{ zIndex }} onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            {Icon && <Icon className={`h-6 w-6 ${iconClass}`} />}
            {title}
          </h2>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">{children}</div>
        {footer && <div className="p-4 border-t border-white/10 flex justify-end gap-2 flex-wrap shrink-0">{footer}</div>}
      </motion.div>
    </div>,
    document.body
  );
};

const Collapsible = ({ icon: Icon, iconClass, title, expanded, onToggle, children }) => (
  <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={20} className={iconClass} />
        <h3 className="text-lg font-semibold text-white truncate">{title}</h3>
      </div>
      {expanded ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
          <div className="p-4 pt-0">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/** إقرار الزكاة — Zakat declaration (replaces TAP) */
export const ZakatDeclarationForm = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.declarationsFiscalesPage.${key}`, opts);
  const [expanded, setExpanded] = useState({ identification: true, calculation: false });
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    fiscalYear: new Date().getFullYear().toString(),
    trn: '',
    currentAssets: 0,
    fixedAssetsNet: 0,
    liabilities: 0,
    zakatBase: 0,
    zakatDue: 0,
  });

  useEffect(() => {
    if (!isOpen) return;
    const base = Math.max(0, formData.currentAssets + formData.fixedAssetsNet - formData.liabilities);
    setFormData((prev) => ({
      ...prev,
      zakatBase: base,
      zakatDue: base * (SAUDI_ZAKAT_RATE / 100),
    }));
  }, [isOpen, formData.currentAssets, formData.fixedAssetsNet, formData.liabilities]);

  const handleSubmit = async () => {
    if (!validateTRN(formData.trn)) {
      window.alert(p('saudi_trn_invalid_alert'));
      return;
    }
    setIsSaving(true);
    try {
      logger.info('Zakat declaration saved', formData);
      window.alert(p('saudi_zakat_saved_alert'));
      onClose();
    } catch (err) {
      logger.error('Zakat save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FiscalModalShell
      open={isOpen}
      onClose={onClose}
      title={p('saudi_zakat_title')}
      Icon={Briefcase}
      iconClass="text-emerald-400"
      footer={(
        <>
          <button type="button" onClick={onClose} className="px-5 py-2 text-white/70 hover:text-white">{p('close')}</button>
          <button type="button" onClick={handleSubmit} disabled={isSaving} className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-700 text-white rounded-xl font-medium flex items-center gap-2 disabled:opacity-50">
            <Save size={16} /> {p('saudi_zakat_save_btn')}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <Collapsible icon={Building} iconClass="text-emerald-400" title={p('saudi_establishment_data')} expanded={expanded.identification} onToggle={() => setExpanded((s) => ({ ...s, identification: !s.identification }))}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={LABEL}><RequiredLabelText text={p('saudi_fiscal_year_required')} /></label>
              <input type="text" value={formData.fiscalYear} onChange={(e) => setFormData({ ...formData, fiscalYear: e.target.value })} className={FIELD} />
            </div>
            <div>
              <label className={LABEL}><RequiredLabelText text={p('saudi_trn_required')} /></label>
              <input type="text" value={formData.trn} onChange={(e) => setFormData({ ...formData, trn: formatTRN(e.target.value) })} className={FIELD} placeholder="300000000000003" maxLength={18} />
            </div>
          </div>
        </Collapsible>
        <Collapsible icon={Calculator} iconClass="text-emerald-400" title={p('saudi_zakat_calculation', { rate: SAUDI_ZAKAT_RATE })} expanded={expanded.calculation} onToggle={() => setExpanded((s) => ({ ...s, calculation: !s.calculation }))}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className={LABEL}>{p('saudi_current_assets')}</label><CustomNumberInput step={0.01} value={formData.currentAssets} onChange={(v) => setFormData({ ...formData, currentAssets: parseFloat(v) || 0 })} className={NUMBER_FIELD} /></div>
            <div><label className={LABEL}>{p('saudi_fixed_assets_net')}</label><CustomNumberInput step={0.01} value={formData.fixedAssetsNet} onChange={(v) => setFormData({ ...formData, fixedAssetsNet: parseFloat(v) || 0 })} className={NUMBER_FIELD} /></div>
            <div><label className={LABEL}>{p('saudi_liabilities')}</label><CustomNumberInput step={0.01} value={formData.liabilities} onChange={(v) => setFormData({ ...formData, liabilities: parseFloat(v) || 0 })} className={NUMBER_FIELD} /></div>
          </div>
          <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
            <div className="flex justify-between text-white/80 mb-2"><span>{p('saudi_zakat_base')}</span><span className="font-mono">{formatBrandMoney(formData.zakatBase)}</span></div>
            <div className="flex justify-between text-emerald-300 font-bold text-lg"><span>{p('saudi_zakat_due', { rate: SAUDI_ZAKAT_RATE })}</span><span className="font-mono">{formatBrandMoney(formData.zakatDue)}</span></div>
          </div>
        </Collapsible>
      </div>
    </FiscalModalShell>
  );
};

/** ضريبة الدخل على الشركات — CIT (replaces IBS/G204) */
export const CorporateIncomeTaxForm = ({ isOpen, onClose, fiscalYearId }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.declarationsFiscalesPage.${key}`, opts);
  const [expanded, setExpanded] = useState({ identification: true, calculation: false });
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    fiscalYear: fiscalYearId || new Date().getFullYear().toString(),
    trn: '',
    accountingProfit: 0,
    adjustments: 0,
    taxableIncome: 0,
    nonSaudiSharePct: 100,
    citDue: 0,
  });

  useEffect(() => {
    const taxable = Math.max(0, formData.accountingProfit + formData.adjustments);
    const cit = taxable * (formData.nonSaudiSharePct / 100) * (SAUDI_CIT_RATE / 100);
    setFormData((prev) => ({ ...prev, taxableIncome: taxable, citDue: cit }));
  }, [formData.accountingProfit, formData.adjustments, formData.nonSaudiSharePct]);

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      logger.info('CIT declaration saved', formData);
      window.alert(p('saudi_cit_saved_alert'));
      onClose();
    } catch (err) {
      logger.error('CIT save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FiscalModalShell open={isOpen} onClose={onClose} title={p('saudi_cit_title')} Icon={FileText} iconClass="text-orange-400"
      footer={(
        <>
          <button type="button" onClick={onClose} className="px-5 py-2 text-white/70 hover:text-white">{p('close')}</button>
          <button type="button" onClick={handleSubmit} disabled={isSaving} className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-700 text-white rounded-xl font-medium flex items-center gap-2 disabled:opacity-50">
            <Save size={16} /> {p('saudi_zakat_save_btn')}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <Collapsible icon={Building} iconClass="text-orange-400" title={p('saudi_identification')} expanded={expanded.identification} onToggle={() => setExpanded((s) => ({ ...s, identification: !s.identification }))}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className={LABEL}>{p('saudi_fiscal_year')}</label><input type="text" value={formData.fiscalYear} onChange={(e) => setFormData({ ...formData, fiscalYear: e.target.value })} className={FIELD} /></div>
            <div><label className={LABEL}>{p('saudi_trn')}</label><input type="text" value={formData.trn} onChange={(e) => setFormData({ ...formData, trn: formatTRN(e.target.value) })} className={FIELD} placeholder="300000000000003" /></div>
          </div>
        </Collapsible>
        <Collapsible icon={Calculator} iconClass="text-orange-400" title={p('saudi_tax_calculation', { rate: SAUDI_CIT_RATE })} expanded={expanded.calculation} onToggle={() => setExpanded((s) => ({ ...s, calculation: !s.calculation }))}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className={LABEL}>{p('saudi_accounting_profit')}</label><CustomNumberInput step={0.01} value={formData.accountingProfit} onChange={(v) => setFormData({ ...formData, accountingProfit: parseFloat(v) || 0 })} /></div>
            <div><label className={LABEL}>{p('saudi_fiscal_adjustments')}</label><CustomNumberInput step={0.01} value={formData.adjustments} onChange={(v) => setFormData({ ...formData, adjustments: parseFloat(v) || 0 })} /></div>
            <div><label className={LABEL}>{p('saudi_non_saudi_share')}</label><CustomNumberInput step={1} min={0} max={100} value={formData.nonSaudiSharePct} onChange={(v) => setFormData({ ...formData, nonSaudiSharePct: parseFloat(v) || 0 })} /></div>
          </div>
          <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl space-y-2">
            <div className="flex justify-between text-white/80"><span>{p('saudi_taxable_income')}</span><span>{formatBrandMoney(formData.taxableIncome)}</span></div>
            <div className="flex justify-between text-orange-300 font-bold text-lg"><span>{p('saudi_cit_due')}</span><span>{formatBrandMoney(formData.citDue)}</span></div>
          </div>
          <p className="text-white/50 text-xs mt-2">{p('saudi_cit_note')}</p>
        </Collapsible>
      </div>
    </FiscalModalShell>
  );
};

/** ضريبة الاستقطاع — Withholding tax (replaces IRG) */
export const WithholdingTaxForm = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.declarationsFiscalesPage.${key}`, opts);
  const [expanded, setExpanded] = useState({ period: true, lines: false });
  const [isSaving, setIsSaving] = useState(false);
  const [period, setPeriod] = useState({ month: String(new Date().getMonth() + 1).padStart(2, '0'), year: String(new Date().getFullYear()) });
  const [lines, setLines] = useState([
    { id: 1, type: 'dividends', rate: SAUDI_WHT_RATES.dividends, base: 0, wht: 0 },
    { id: 2, type: 'royalties', rate: SAUDI_WHT_RATES.royalties, base: 0, wht: 0 },
    { id: 3, type: 'technical', rate: SAUDI_WHT_RATES.technical_services, base: 0, wht: 0 },
    { id: 4, type: 'management', rate: SAUDI_WHT_RATES.management_fees, base: 0, wht: 0 },
  ]);

  const whtLineLabel = (type) => {
    const keyMap = {
      dividends: 'saudi_wht_dividends',
      royalties: 'saudi_wht_royalties',
      technical: 'saudi_wht_technical',
      management: 'saudi_wht_management',
    };
    return p(keyMap[type] || type);
  };

  const updateLine = (id, base) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, base, wht: base * (l.rate / 100) } : l)));
  };

  const totalWht = lines.reduce((s, l) => s + l.wht, 0);

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      logger.info('WHT declaration saved', { period, lines, totalWht });
      window.alert(p('saudi_wht_saved_alert'));
      onClose();
    } catch (err) {
      logger.error('WHT save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: new Date(2025, i, 1).toLocaleDateString('ar-SA', { month: 'long' }),
    isDefault: true,
  }));

  return (
    <FiscalModalShell open={isOpen} onClose={onClose} title={p('saudi_wht_title')} Icon={Users} iconClass="text-green-400"
      footer={(
        <>
          <button type="button" onClick={onClose} className="px-5 py-2 text-white/70 hover:text-white">{p('close')}</button>
          <button type="button" onClick={handleSubmit} disabled={isSaving} className="px-5 py-2 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-xl font-medium flex items-center gap-2 disabled:opacity-50">
            <Send size={16} /> {p('saudi_wht_submit_btn')}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <Collapsible icon={FileText} iconClass="text-green-400" title={p('saudi_period')} expanded={expanded.period} onToggle={() => setExpanded((s) => ({ ...s, period: !s.period }))}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className={LABEL}>{p('saudi_month')}</label><CustomSelect value={period.month} onChange={(v) => setPeriod({ ...period, month: v })} options={months} searchable={false} autoSelect={false} /></div>
            <div><label className={LABEL}>{p('saudi_year')}</label><input type="text" value={period.year} onChange={(e) => setPeriod({ ...period, year: e.target.value })} className={FIELD} /></div>
          </div>
        </Collapsible>
        <Collapsible icon={Calculator} iconClass="text-green-400" title={p('saudi_wht_payments')} expanded={expanded.lines} onToggle={() => setExpanded((s) => ({ ...s, lines: !s.lines }))}>
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-white/70"><th className="py-2 text-right">{p('saudi_wht_col_type')}</th><th className="py-2 text-right">{p('saudi_wht_col_rate')}</th><th className="py-2 text-right">{p('saudi_wht_col_base')}</th><th className="py-2 text-right">{p('saudi_wht_col_withholding')}</th></tr></thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} className="border-b border-white/5">
                    <td className="py-2 text-white">{whtLineLabel(line.type)}</td>
                    <td className="py-2 text-right text-white/70">{line.rate}%</td>
                    <td className="py-2"><CustomNumberInput step={0.01} value={line.base} onChange={(v) => updateLine(line.id, parseFloat(v) || 0)} /></td>
                    <td className="py-2 text-right text-green-400 font-mono">{formatBrandMoney(line.wht)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Collapsible>
        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex justify-between items-center">
          <span className="text-white font-semibold">{p('saudi_wht_total')}</span>
          <span className="text-2xl font-bold text-green-300 font-mono">{formatBrandMoney(totalWht)}</span>
        </div>
      </div>
    </FiscalModalShell>
  );
};
